import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setToken, setUser } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

export function LoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.full_name || data.user.email}`);
      if (data.user.role === "super_admin") nav("/admin");
      else nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-200 px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="back-to-home">
          <ArrowLeft size={16} /> Back
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <Logo className="h-9 mb-8" />
            <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">Sign in</h1>
            <p className="text-sm text-gray-500 mt-2">Access your ExpoFlo AI workspace.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="you@company.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 text-white text-sm font-medium rounded-md py-2.5 hover:bg-gray-800 disabled:opacity-60 transition-colors"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-xs text-gray-400 leading-relaxed">
            Access is invite-only. New organizations are onboarded manually by the ExpoFlo team.
            <br />
            <Link to="/book-demo" className="text-purple-600 hover:text-purple-800">Request access →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
